import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import { CancellationFlowStep } from 'store/dashboard/types';
import { useDispatch, useSelector } from 'react-redux';
import { setCancellationFlowStep } from 'store/dashboard/slice';
import fetchCancellationReasonsOperation from 'store/dashboard/operation/fetchCancellationReasonsOperation';
import getCancellationReasons from 'store/dashboard/selector/getCancellationReasons';
import OpRadio from 'components/Common/op-ui-wrappers/OpRadio/OpRadio';
import OpText from 'components/Common/op-ui-wrappers/OpText/OpText';
import OpButton from 'components/Common/op-ui-wrappers/OpButton/OpButton';
import cancelSubscriptionOperation from 'store/dashboard/operation/cancelSubscriptionOperation';
import AdditionalOptions from '../AdditionalOptions/AdditionalOptions';
import styles from './CancellationReasons.module.scss';

const CancellationReasons = () => {
  const dispatch = useDispatch();
  const reasons = useSelector(getCancellationReasons);
  const [selectedReason, setSelectedReason] = useState<string>();
  const [otherReason, setOtherReason] = useState('');

  const setStep = (step: CancellationFlowStep) => dispatch(setCancellationFlowStep(step));

  const cancelSubscription = async () => {
    const cancellationReason = selectedReason === 'other' ? otherReason : selectedReason;

    if (!cancellationReason) {
      return;
    }
    const cancellationOperation =
      selectedReason === 'other'
        ? cancelSubscriptionOperation('other', otherReason)
        : cancelSubscriptionOperation(cancellationReason);

    await dispatch(cancellationOperation);
  };

  const showTextArea = selectedReason === 'other';
  const cancelButtonDisabled = selectedReason === 'other' ? !otherReason : !selectedReason;

  useEffect(() => {
    dispatch(fetchCancellationReasonsOperation());
  }, []);

  if (!reasons) {
    return null;
  }

  return (
    <div className={styles.container}>
      <h1 className={styles.heading}>{reasons.title}</h1>
      <div className={styles.subHeading}>{reasons.subtTitle}</div>
      <div className={styles.options}>
        {reasons.reasonsOptions.map(({ key, option }) => (
          <div className={styles.option} key={`reason_${key}`}>
            <OpRadio
              key={option}
              className={styles.dropdownOption}
              checked={selectedReason === key}
              onClick={() => setSelectedReason(key)}
            >
              <OpText slot="heading" variant="body-large-regular" className={styles.reasonText}>
                {option}
              </OpText>
            </OpRadio>
          </div>
        ))}
      </div>
      {showTextArea && (
        <textarea
          className={styles.textarea}
          name="other_reason"
          placeholder={reasons.helpTextPlaceHolder}
          onChange={(e) => setOtherReason(e.target.value)}
        />
      )}
      <div className={styles.actions}>
        <OpButton
          onClick={() => cancelSubscription()}
          block
          variant="secondary"
          color="teal"
          className={cn({ [styles['action--disabled']]: cancelButtonDisabled })}
        >
          <OpText variant="body-large-medium" className={cn(styles.action, styles['action--secondary'])}>
            {reasons.cancelButton}
          </OpText>
        </OpButton>
        <OpButton onClick={() => setStep(CancellationFlowStep.initial)} block variant="primary" color="teal">
          <OpText variant="body-large-medium" className={cn(styles.action, styles['action--primary'])}>
            {reasons.keepButton}
          </OpText>
        </OpButton>
      </div>
      <hr className={styles.devider} />
      <div className={styles.additionalOptions}>
        <AdditionalOptions
          contactCsTxt={reasons.txtContactCs}
          contactCsUrl={reasons.txtContactCsUrl}
          helpText={reasons.txtOptionsNeedHelp}
          title={reasons.txtOptionsTitle}
        />
      </div>
    </div>
  );
};

export default CancellationReasons;
